// CONTRACT:I2-MARKING-NORMALIZER.1.0
/** Marking NORMALIZATION pre-processor — maps a CLOSED set of KNOWN INFORMAL
 *  markings to FORMAL canonical attribute values, BEFORE the security decision.
 *
 *  This is NOT an evaluator (that is `CONTRACT:I4-POLICY-DECISION.1.0`): it reads no
 *  subject, makes no allow/deny call, touches no key. It only canonicalises an
 *  object's OWN marking vocabulary so each token routes to the right attribute in
 *  `deriveBoundFqns` instead of being silently dropped.
 *
 *  THE FAIL-SAFE (load-bearing): the normalizer may ONLY rewrite a token to a value
 *  that is a known canonical key in `vocabMap` — every such value is sourced from the
 *  LIVE policy bundle's own `values`, so the normalizer can NEVER invent a token, never
 *  widen an object beyond the attributes its raw markings already named, and never
 *  promote an unknown token. An UNKNOWN token passes through UNCHANGED (it stays
 *  unmatched downstream and is still dropped). The only effect is to RESCUE tokens that
 *  were already meant to bind but arrived informally decorated (e.g. `SAP ALPHA`,
 *  `compartment:SI-G`). See architecture/CONTRACT-I2-MARKING-NORMALIZER.1.0.md.
 *
 *  HISTORY (ARC 1, 2026-06-25): the adapter used to emit a `REL:${code}` caveat for
 *  coalition releasability (the original silent-drop bug). That trick is GONE — the
 *  adapter now maps FVEY/NATO/coalition codes DIRECTLY to the canonical `releasability`
 *  attribute, so no `REL:` decoration is emitted anymore. The remaining rescue keeps the
 *  still-used informal SCI/SAP/compartment decorations (`sap:`, `compartment:`, `SAP <name>`)
 *  that corpus-authored mission/caveat tokens arrive with — a generic, fail-safe rewrite. */

import type { PolicyBundle } from "./types.ts";
import type { SecurityView } from "./fqn.ts";

/** Lower-cased INFORMAL token → FORMAL canonical attribute value. Every right-hand
 *  (canonical) value is a real value in the governing bundle, so the map can only ever
 *  PRODUCE a known canonical key — never invent one. */
export type VocabMap = Map<string, string>;

/** The informal "decorations" we have seen wrapped around an otherwise-canonical
 *  token in the wild. Each, applied to a canonical bundle value, yields an informal
 *  KEY that maps BACK to that same canonical value. We deliberately enumerate a CLOSED
 *  set (no open-ended regex that could canonicalise an unrelated token). */
const PREFIX_DECORATIONS = [
  // `<prefix>:<value>` — the colon-prefixed forms emitted by adapters/feeds.
  (v: string) => `sci:${v}`,
  (v: string) => `compartment:${v}`,
  (v: string) => `sap:${v}`,
  (v: string) => `accm:${v}`,
  (v: string) => `dissem:${v}`,
  (v: string) => `control:${v}`,
  (v: string) => `caveat:${v}`,
  (v: string) => `program:${v}`,
  // `rel:` / `rel ` family — RESTORED: the gateway PROTECT leg + arbitrary TAK feeds still emit
  // REL:-decorated releasability tokens (the corpus adapter no longer does). The normalizer is
  // fail-safe — it can only rewrite a decoration to a value ALREADY in the live vocabMap, never
  // inventing or widening — so this keeps the gateway REL:FVEY / REL NATO lockstep green at zero cost.
  (v: string) => `rel:${v}`,
  (v: string) => `rel ${v}`,
  (v: string) => `rel to ${v}`,
  (v: string) => `rel-to ${v}`,
  // `<prefix> <value>` — the space-separated banner forms (e.g. "SAP ALPHA").
  (v: string) => `sap ${v}`,
  (v: string) => `sci ${v}`,
  (v: string) => `compartment ${v}`,
  (v: string) => `accm ${v}`,
] as const;

/** Canonicalisation key for a raw token: trimmed, lower-cased, inner whitespace
 *  collapsed. Used for BOTH map keys (informal forms) and lookups (raw tokens) so the
 *  match is case/space tolerant without any value being invented. */
function vocabKey(token: string): string {
  return String(token).trim().replace(/\s+/g, " ").toLowerCase();
}

/** Build the CLOSED informal→canonical map from a LIVE policy bundle. The canonical
 *  side is the bundle's OWN `values` (never a stale seed), so the map is correct for
 *  whatever bundle is live and can ONLY produce values that exist in that bundle.
 *
 *  For each canonical value V we register:
 *    - the bare canonical form `v` → V (idempotent: already-canonical tokens stay V), and
 *    - every informal DECORATION of V (REL:V, SAP V, prefix:V, …) → V.
 *  We NEVER register a key that is unrelated to a bundle value, so an unknown raw token
 *  finds no entry and is passed through unchanged. */
export function buildVocabMap(bundle: PolicyBundle): VocabMap {
  const map: VocabMap = new Map();
  // Collect every canonical value across every attribute (the closed universe).
  const canonical = new Set<string>();
  for (const attr of bundle.attributes) {
    for (const v of attr.values) canonical.add(v);
  }
  for (const v of canonical) {
    const canonKey = vocabKey(v);
    if (!canonKey) continue;
    // Bare canonical form (keeps the function idempotent on already-canonical input).
    map.set(canonKey, v);
    // Every known informal decoration of this canonical value maps BACK to it.
    for (const decorate of PREFIX_DECORATIONS) {
      const informal = vocabKey(decorate(canonKey));
      // First-writer-wins keeps the map deterministic; a decoration never overrides a
      // bare canonical key (a real value always beats a coincidental decoration).
      if (!map.has(informal)) map.set(informal, v);
    }
  }
  return map;
}

/** Canonicalise ONE raw token: if its key is in `vocabMap`, return the canonical value;
 *  otherwise return the ORIGINAL token unchanged (the fail-safe — never invent). */
function normalizeToken(token: string, vocabMap: VocabMap): string {
  if (typeof token !== "string") return token;
  const hit = vocabMap.get(vocabKey(token));
  return hit ?? token;
}

/** Canonicalise a list field, preserving order and length (1:1 per-token rewrite). */
function normalizeList(list: string[] | undefined, vocabMap: VocabMap): string[] | undefined {
  if (list === undefined) return undefined;
  return list.map((t) => normalizeToken(t, vocabMap));
}

/** PURE: return a COPY of `raw` with each marking token canonicalised IFF its canonical
 *  form is a known key in `vocabMap`; unknown tokens are returned UNCHANGED. Never adds,
 *  removes, or reorders fields beyond per-token rewriting; never mutates `raw`.
 *
 *  Runs strictly BEFORE `deriveBoundFqns` (CONTRACT:I4-POLICY-DECISION.1.0). */
export function normalizeSecurityView(raw: SecurityView, vocabMap: VocabMap): SecurityView {
  const out: SecurityView = { ...raw };
  if (raw.classification !== undefined) {
    out.classification = normalizeToken(raw.classification, vocabMap);
  }
  const rel = normalizeList(raw.releasability, vocabMap);
  if (rel !== undefined) out.releasability = rel;
  const cav = normalizeList(raw.caveats, vocabMap);
  if (cav !== undefined) out.caveats = cav;
  const mis = normalizeList(raw.mission, vocabMap);
  if (mis !== undefined) out.mission = mis;
  return out;
}
